import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #observer = new IntersectionObserver((entries) => {
    this.#visible = entries.some((entry) => entry.isIntersecting);
    this.#updatePlayback();
  }, { rootMargin: '200px 0px' });
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    this.#observer.observe(this);
    this.#reducedMotion.addEventListener('change', this.#updatePlayback);
    document.addEventListener('visibilitychange', this.#updatePlayback);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.#visible = false;
    this.#reducedMotion.removeEventListener('change', this.#updatePlayback);
    document.removeEventListener('visibilitychange', this.#updatePlayback);
    this.refs.videoElement.pause();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updatePlayback();
  }

  #updatePlayback = () => {
    const { videoSources, videoElement } = this.refs;

    if (!this.isConnected || !this.#visible || this.#reducedMotion.matches || document.hidden) {
      videoElement.pause();
      return;
    }

    let sourcesChanged = false;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource && source.getAttribute('src') !== videoSource) {
        source.setAttribute('src', videoSource);
        sourcesChanged = true;
      }
    }

    if (sourcesChanged) videoElement.load();
    if (videoElement.paused) videoElement.play().catch(() => {});
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
